import { useSticky } from "@/hooks/useSticky";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import {
  generateNavigationUrl,
  playersSelector,
} from "@/redux/players/playersSlice";
import { Tactic } from "@/redux/tactics/tacticsSlice";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { deleteSavedSquad } from "../../../api/privateRequests/squad-builder/delete-squad";
import { MetaRating } from "../../../api/privateRequests/squad-builder/get-meta-ratings";
import { CenterPanel } from "../../../components/features/SquadBuilder/CenterPanel/CenterPanel";
import { LeftPanel } from "../../../components/features/SquadBuilder/LeftPanel/LeftPanel";
import { RightPanel } from "../../../components/features/SquadBuilder/RightPanel/RightPanel";
import {
  ActiveFilter,
  FilterKey,
} from "../../../components/features/SquadBuilder/components/FiltersBar/FiltersBar";
import { Options as PlayerActiveBarOptions } from "../../../components/features/SquadBuilder/components/PlayerActiveBarMobile/PlayerActiveBarMobile";
import { UpdateFilterType } from "../../../components/organisms/Filters/PlayerFilters/PlayerFilters";
import { convertSBCPrice } from "../../../functions/convertPlayerPrice";
import {
  getFormationsLabel,
  loadedContentSelector,
} from "../../../redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "../../../redux/message/messageSlice";
import {
  addPlayerOrChangeRole,
  addSquad,
  buildSquads,
  clearCustomFilter,
  clearFilter,
  clearPlayerFilters,
  clearRecommendedPlayers,
  clearSquad,
  defaultSquadBuilderSelector,
  exitPickSquad,
  fetchRecommendedPlayers,
  fetchSpecificSquad,
  fetchSquads,
  getCodeFromTactic,
  getPlayStyleFilterOptions,
  getTacticFromCode,
  importSquad,
  movePlayer,
  numberOfPlayersWithFiltersSelector,
  removePlayer,
  removeTempSquad,
  resetRole,
  saveSquad,
  selectChemStyle,
  setChosenTactic,
  setSaveSquadStatusToIdle,
  sortBuiltSquads,
  switchLeftPanelMode,
  switchRightPanelMode,
  updateBudget,
  updateChangingPitchItemOnIndex,
  updateCustomFilter,
  updateExcludeIconsHeroes,
  updateFormation,
  updateIncludeLoanPlayers,
  updateLoadedSquadIndex,
  updateManager,
  updateMaxRating,
  updateMinRating,
  updateScope,
  updateSelectedClubs,
  updateSelectedCountries,
  updateSelectedLeagues,
  updateSelectedMetals,
  updateSelectedPnPs,
  updateSelectedVersions,
} from "../../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import {
  Scope,
  Squad,
  SquadBuilderPlayerType,
} from "../../../redux/squadBuilder/squadBuilderUtils";
import { AppDispatch } from "../../../redux/store";
import { User } from "../../../redux/user/userSlice";
import { MobileWrapper } from "../MobileWrapper/MobileWrapper";

type Props = {
  user: User;
  desktopMode: boolean;
  tabletOrLargerMode: boolean;
};

export const DefaultSquadBuilder = ({
  user,
  desktopMode,
  tabletOrLargerMode,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    tempSquad,
    squad,
    benchItems,
    tempBenchItems,
    statusBuildSquad,
    statusGetRecommendedPlayers,
    budget,
    excludeIconsHeroes,
    ratingFilter,
    scope,
    selectedCountries,
    selectedLeagues,
    selectedClubs,
    selectedVersions,
    selectedMetals,
    changingPitchItemOnIndex,
    recommendedPlayers,
    rightPanelMode,
    builtSquads,
    statusImportSquad,
    playerFilters,
    leftPanelMode,
    chosenTactic,
    statusSaveSquad,
    statusFetchSavedSquads,
    statusInsertTacticCode,
    savedSquads,
    loadedSaveSquadIndex,
    hasChangedSquad,
    selectedPnPs,
    tempFormation,
    includeLoanPlayers,
  } = useSelector(defaultSquadBuilderSelector);
  const { chemistryProfilesStatus, formations } = useSelector(
    loadedContentSelector,
  );
  const { initialPlayerDataStatus, playerRoles } = useSelector(playersSelector);
  const numberOfPlayersWithFilters = useSelector(
    numberOfPlayersWithFiltersSelector,
  );
  const formationLabel = useSelector(
    getFormationsLabel(chosenTactic?.teamTactics.formationId),
  );
  const [showLeftPanelMobile, setShowLeftPanelMobile] = useState(false);
  const [showRightPanelMobile, setShowRightPanelMobile] = useState(false);
  const [currentBuiltSquadsIndex, setCurrentBuildSquadsIndex] = useState(-1);
  const [playerActiveBar, setPlayerActiveBar] =
    useState<PlayerActiveBarOptions>("none");
  const showLeftPanel = desktopMode || showLeftPanelMobile;
  const showRightPanel = desktopMode || showRightPanelMobile;
  const navigate = useNavigate();
  const [showModals, setShowModals] = useState<"save">(undefined);
  const [squadName, setSquadName] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const topToPage = useSelector(topToPageSelector);
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: topToPage.value,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  useEffect(() => {
    if (changingPitchItemOnIndex === -1) {
      setPlayerActiveBar("none");
    }
  }, [changingPitchItemOnIndex]);

  const handleLoadSquads = () => dispatch(fetchSquads());

  useEffect(() => {
    if (user.uuid && statusFetchSavedSquads === "idle") {
      handleLoadSquads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uuid, statusFetchSavedSquads, statusSaveSquad, dispatch]);

  useEffect(() => {
    if (initialPlayerDataStatus === "fulfilled") {
      dispatch(getPlayStyleFilterOptions());
    }
  }, [dispatch, initialPlayerDataStatus]);

  useEffect(() => {
    if (statusSaveSquad === "fulfilled") {
      dispatch(
        addAlert({
          type: "success",
          description: `Successfully saved squad`,
        }),
      );
    }
  }, [statusSaveSquad, dispatch]);

  useEffect(() => {
    const fetchSharedSquad = async (id: string) => {
      try {
        await dispatch(
          fetchSpecificSquad({ squadId: id, sharedOrSaved: "shared" }),
        );
        dispatch(
          addAlert({
            type: "success",
            description: "Succesfully loaded squad",
          }),
        );
      } catch (e) {
        dispatch(
          addAlert({
            type: "error",
            description: "Failed to load squad",
          }),
        );
      } finally {
        setSearchParams("");
      }
    };

    const squadId = searchParams.get("id");
    if (squadId && chemistryProfilesStatus === "fulfilled") {
      fetchSharedSquad(squadId);
    }
  }, [searchParams, setSearchParams, dispatch, chemistryProfilesStatus]);

  useEffect(() => {
    const fetchSavedSquadFromLogin = async (id: string) => {
      try {
        await dispatch(
          fetchSpecificSquad({ squadId: id, sharedOrSaved: "saved" }),
        );
        dispatch(
          addAlert({
            type: "success",
            description: "Succesfully loaded squad",
          }),
        );
        const res = await dispatch(fetchSquads());
        dispatch(updateLoadedSquadIndex((res.payload as []).length - 1));
      } catch (e) {
        dispatch(
          addAlert({
            type: "error",
            description: "Failed to load squad",
          }),
        );
      } finally {
        setSearchParams("");
      }
    };

    const squadId = searchParams.get("saved_squad_id");
    if (squadId && user.uuid && chemistryProfilesStatus === "fulfilled") {
      fetchSavedSquadFromLogin(squadId);
    }
  }, [searchParams, setSearchParams, dispatch, chemistryProfilesStatus, user]);

  const handleAddSquad = (index: number, temp: boolean) => {
    dispatch(addSquad({ index, temp: temp }));
    if (!temp) {
      dispatch(switchRightPanelMode("default"));
    }
  };

  useEffect(() => {
    dispatch(getCodeFromTactic({ tactic: chosenTactic }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenTactic.teamTactics, chosenTactic.playerRoles]);

  useEffect(() => {
    if (statusBuildSquad === "fulfilled" && !desktopMode) {
      setCurrentBuildSquadsIndex(0);
      handleAddSquad(0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusBuildSquad, desktopMode]);

  const handleChangePlayerRole = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    dispatch(switchRightPanelMode("add-player-role"));
    setPlayerActiveBar("role");
    setShowRightPanelMobile(true);
  };

  const handleRemovePlayerOrRole = (index: number) => {
    if (index > 10 || squad.players[index]?.resourceId) {
      dispatch(removePlayer({ index }));
      dispatch(updateChangingPitchItemOnIndex(-1));
    } else if (tempSquad.players[index]?.resourceId) {
      // fallback in case a temp player is somehow present
      dispatch(removePlayer({ index, temp: true }));
      dispatch(updateChangingPitchItemOnIndex(-1));
    } else {
      dispatch(resetRole(index));
    }
  };

  const handleAddPlayer = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    dispatch(switchRightPanelMode("add-player"));
    setPlayerActiveBar("insert");
    setShowRightPanelMobile(true);
    if (!desktopMode) {
      window.scrollTo(0, 104);
    }
  };

  const handleClickedClose = () => {
    if (desktopMode || !squad.players[changingPitchItemOnIndex]?.resourceId) {
      dispatch(updateChangingPitchItemOnIndex(-1));
    } else {
      setPlayerActiveBar("none");
    }
    dispatch(switchRightPanelMode("default"));
    dispatch(clearRecommendedPlayers());
    setShowRightPanelMobile(false);
  };

  const handleClickedClosePlayerFiltersMode = () => {
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
    dispatch(updateChangingPitchItemOnIndex(-1));
  };

  const handleClickedCloseMoreFiltersMode = () => {
    dispatch(switchRightPanelMode("default"));
  };

  const handleCloseSuggestedSquadsMode = () => {
    dispatch(exitPickSquad());
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
  };

  const handleClickedCloseEvolvePlayerMode = () => {
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
    dispatch(updateChangingPitchItemOnIndex(-1));
  };

  const handleClickedCloseChemstyleMode = () => {
    dispatch(switchRightPanelMode("default"));
    setShowRightPanelMobile(false);
    dispatch(updateChangingPitchItemOnIndex(-1));
  };

  const handleGetPlayerRecommendations = () => {
    dispatch(fetchRecommendedPlayers());
  };

  const handleDidAddPlayer = (
    playerRoleId?: number,
    player?: SquadBuilderPlayerType,
    temp?: boolean,
    metaRatings?: MetaRating[],
    focus?: string,
  ) => {
    dispatch(
      addPlayerOrChangeRole({
        playerRoleId,
        player,
        temp: tabletOrLargerMode ? temp : false,
        metaRatings,
        focus,
      }),
    );
    if (!temp && player?.resourceId && desktopMode) {
      handleClickedClose();
    }
    if (player?.resourceId && !temp && !desktopMode) {
      setShowRightPanelMobile(false);
      setPlayerActiveBar("none");
      if (window.scrollY > 104) {
        window.scrollTo(0, 104);
      } else if (changingPitchItemOnIndex >= 10) {
        window.scrollTo(0, 400);
      }
    }
  };

  const handleUpdateFormation = (formationId: string, isTemp: boolean) =>
    dispatch(
      updateFormation({
        formationId,
        isTemp: desktopMode ? isTemp : false,
      }),
    );

  const handleBuildSquadFulfilled = () => {
    dispatch(switchRightPanelMode("suggested-squads"));
  };

  const handleClickedBuildSquads = () => {
    if (user.playerCount === 0 && (scope === "club" || scope === "both")) {
      dispatch(
        addAlert({
          type: "error",
          description: "You need to import players first",
        }),
      );
    } else {
      dispatch(buildSquads());
    }
  };

  const handleScopeChange = (newScope: Scope) => {
    if (user?.subscription !== "diamond") {
      dispatch(
        showModal({
          modalType: "premium-feature",
          premiumFeatureHook: "build-squad-with-club",
        }),
      );
    } else {
      dispatch(updateScope(newScope));
    }
  };

  const handleSelectedCountry = (id: number) =>
    dispatch(updateSelectedCountries(id));

  const handleSelectedLeague = (id: number) =>
    dispatch(updateSelectedLeagues(id));

  const handleSelectedClub = (id: number) => dispatch(updateSelectedClubs(id));

  const handleSelectedPastAndPresent = (id: number) => {
    dispatch(updateSelectedPnPs(id));
  };

  const handleSelectedVersion = (id: number) =>
    dispatch(updateSelectedVersions(id));

  const handleSelectedMetal = (id: number) =>
    dispatch(updateSelectedMetals(id));

  const handleBudgetUpdated = (budget: number) =>
    dispatch(updateBudget(budget));

  const handleExcludeIconsHeroesUpdated = (excludeIconsHeroes: boolean) =>
    dispatch(updateExcludeIconsHeroes(excludeIconsHeroes));

  const handleIncludeLoanPlayersUpdated = (includeLoanPlayers: boolean) => {
    if (user?.subscription !== "diamond") {
      dispatch(
        showModal({
          modalType: "premium-feature",
        }),
      );
    } else {
      dispatch(updateIncludeLoanPlayers(includeLoanPlayers));
    }
  };

  const handleMinRatingUpdated = (minRating: number) =>
    dispatch(updateMinRating(minRating));

  const handleMaxRatingUpdated = (maxRating: number) =>
    dispatch(updateMaxRating(maxRating));

  const handleMovePlayer = (fromIndex: number, toIndex: number) => {
    dispatch(
      movePlayer({
        fromIndex,
        toIndex,
      }),
    );
  };

  const handleRemoveTempPlayer = () => dispatch(removePlayer({ temp: true }));

  const handleSortBuiltSquads = (sortBy: "meta-rating" | "chemistry") =>
    dispatch(sortBuiltSquads(sortBy));

  const handleRemoveTempSquad = () => dispatch(removeTempSquad());

  const handleImportSquad = () => {
    if (user?.subscription !== "diamond") {
      dispatch(
        showModal({
          modalType: "premium-feature",
          premiumFeatureHook: "build-squad-with-club",
        }),
      );
    } else {
      dispatch(importSquad());
    }
  };

  const handleSelectManager = (id: number, type: "country" | "league") =>
    dispatch(
      updateManager({
        id,
        type,
      }),
    );

  const handleChangeChemstyle = (index: number) => {
    dispatch(switchRightPanelMode("select-chem-style"));
    dispatch(updateChangingPitchItemOnIndex(index));
  };

  const handleSelectChemstyle = (
    chemstyleId: number,
    metaRatings: MetaRating[],
  ) => {
    dispatch(
      selectChemStyle({
        chemstyleId,
        metaRatings,
      }),
    );
  };

  const handleAddEvoPlayer = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    dispatch(switchRightPanelMode("add-evo-player"));
  };

  const handleAddPlayerFilters = (index: number) => {
    dispatch(updateChangingPitchItemOnIndex(index));
    if (desktopMode) {
      dispatch(switchRightPanelMode("add-player-filters"));
    } else {
      handlePlayerActiveBar("filter");
    }
  };

  const handleClearPlayerFilters = async (index: number) => {
    dispatch(clearPlayerFilters(index));
    dispatch(getPlayStyleFilterOptions());
  };

  const handleUpdateFilter = (f: UpdateFilterType) => {
    dispatch(updateCustomFilter(f));
  };

  const handleClearCustomFilter = (key: string) => {
    dispatch(clearCustomFilter(key));
  };

  const handleClearFilter = (key: FilterKey) => {
    dispatch(clearFilter(key));
  };

  const handleSwitchLeftPanelMode = (type: "tactics" | "overview") => {
    if (type === "tactics") {
      dispatch(switchLeftPanelMode("tactics"));
    } else {
      dispatch(switchLeftPanelMode("chem-overview"));
    }
  };

  const handleOnClickPlayerMobile = (index: number) => {
    dispatch(
      updateChangingPitchItemOnIndex(
        changingPitchItemOnIndex === index ? -1 : index,
      ),
    );
    setPlayerActiveBar("none");
  };

  const handleOnClickPitchMobile = () =>
    dispatch(updateChangingPitchItemOnIndex(-1));

  const handleClickSquadSummaryMobile = () => {
    setShowLeftPanelMobile(
      leftPanelMode !== "chem-overview" || !showLeftPanelMobile,
    );
    setShowRightPanelMobile(false);
    setPlayerActiveBar("none");
    dispatch(switchLeftPanelMode("chem-overview"));
  };

  const handleClickFiltersSummaryMobile = () => {
    setShowRightPanelMobile(
      rightPanelMode !== "default" || !showRightPanelMobile,
    );
    setShowLeftPanelMobile(false);
    setPlayerActiveBar("none");
    dispatch(switchRightPanelMode("default"));
  };

  const handleClickTacticSummaryMobile = () => {
    setShowLeftPanelMobile(leftPanelMode !== "tactics" || !showLeftPanelMobile);
    setShowRightPanelMobile(false);
    setPlayerActiveBar("none");
    dispatch(switchLeftPanelMode("tactics"));
  };

  const handleOnClickPlayer = ({
    name,
    resourceId,
    playerRoleId,
    chemistry,
    eaPlayerId,
    evoPathId,
    evoPath,
    chemstyleId,
    canEvolve,
  }: {
    name: string;
    resourceId: number;
    playerRoleId: number;
    chemistry: number;
    eaPlayerId?: number;
    evoPathId?: number;
    evoPath?: number[];
    chemstyleId?: number;
    canEvolve?: boolean;
  }) => {
    const navLink = generateNavigationUrl({
      name,
      resourceId,
      playerRoleId,
      chemistry,
      eaPlayerId,
      evoPathId,
      evolutionPath: evoPath,
      chemstyleId,
      canEvolve,
    });
    navigate(navLink, {
      state: {
        from: "/squad-builder",
        scrollPosition: window.scrollY,
        chemistryContext: chemistry,
        chemStyleContext: chemstyleId,
      },
    });
  };

  const noSelectedActivePlayerBarItem = () => {
    setPlayerActiveBar("none");
    setShowRightPanelMobile(false);
    setShowLeftPanelMobile(false);
  };

  const handlePlayerActiveBar = (option: PlayerActiveBarOptions) => {
    switch (option) {
      case "role":
        if (playerActiveBar === "role") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("role");
          dispatch(switchRightPanelMode("add-player-role"));
        }
        break;
      case "chemistry":
        if (playerActiveBar === "chemistry") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("chemistry");
          dispatch(switchRightPanelMode("select-chem-style"));
          // setDetailedPlayerViewParams({
          //   resourceId: undefined,
          // });
        }
        break;
      case "insert":
        if (playerActiveBar === "insert") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("insert");
          dispatch(switchRightPanelMode("add-player"));
          // setDetailedPlayerViewParams({
          //   resourceId: undefined,
          // });
        }
        break;
      case "details":
        if (playerActiveBar === "details") {
          noSelectedActivePlayerBarItem();
        } else {
          setPlayerActiveBar("details");
          const clickedPlayer = tempSquad.players[changingPitchItemOnIndex]
            ?.resourceId
            ? tempSquad.players[changingPitchItemOnIndex]
            : squad.players[changingPitchItemOnIndex];
          handleOnClickPlayer({
            name: clickedPlayer.cardName,
            resourceId: clickedPlayer.resourceId,
            playerRoleId: clickedPlayer.playerRoleId,
            chemistry: clickedPlayer.chemistry || 0,
            eaPlayerId: clickedPlayer.eaPlayerId,
            evoPathId: clickedPlayer.evoPathId,
            evoPath: clickedPlayer.evolutionPath,
            chemstyleId: clickedPlayer.chemstyleId,
            canEvolve: clickedPlayer.canEvolve,
          });
        }
        break;
      case "evolve":
        if (playerActiveBar === "evolve") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("evolve");
          dispatch(switchRightPanelMode("add-evo-player"));
        }
        break;
      case "filter":
        if (playerActiveBar === "filter") {
          noSelectedActivePlayerBarItem();
        } else {
          setShowRightPanelMobile(true);
          setPlayerActiveBar("filter");
          dispatch(switchRightPanelMode("add-player-filters"));
        }
        break;
    }
  };

  const handleCurrentBuiltSquadsIndex = (index: number) => {
    let newIndex: number;
    if (index < 0) {
      newIndex = builtSquads.length - 1;
    } else if (index === builtSquads.length) {
      newIndex = 0;
    } else {
      newIndex = index;
    }
    handleAddSquad(newIndex, true);
    setCurrentBuildSquadsIndex(newIndex);
  };

  const handleSelectedBuildSquads = () => {
    handleAddSquad(currentBuiltSquadsIndex, false);
    setCurrentBuildSquadsIndex(-1);
  };

  const handleClickedCancelBuildSquads = () => {
    dispatch(exitPickSquad());
    setCurrentBuildSquadsIndex(-1);
    dispatch(removeTempSquad());
  };

  const defineSquadData = ({
    chosenTactic,
    benchItems,
    squad,
  }: {
    chosenTactic: Tactic;
    benchItems: SquadBuilderPlayerType[];
    squad: Squad;
  }) => {
    return {
      formation: chosenTactic.teamTactics.formationId,
      players: [
        ...squad.players.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          evoPath: player.evolutionPath?.join("-"),
          evoPathId: player.evoPathId,
        })),
        ...benchItems.map((player) => ({
          playerRoleId: player.playerRoleId,
          resourceId: player.resourceId,
          evoPath: player.evolutionPath?.join("-"),
          evoPathId: player.evoPathId,
        })),
      ],
      manager: {
        countryId: squad.manager?.country,
        leagueId: squad.manager?.league,
      },
      tacticId: "",
      tactic: {
        name: chosenTactic.name,
        description: chosenTactic.description,
        teamTactics: {
          buildUpStyle: chosenTactic.teamTactics.buildUpStyle,
          formationId: chosenTactic.teamTactics.formationId,
          defensiveApproach: chosenTactic.teamTactics.defensiveApproach,
          lineHeight: chosenTactic.teamTactics.lineHeight,
        },
        logoUrl: chosenTactic.logoUrl,
        playerRoles: chosenTactic.playerRoles.map((p) => ({
          playerRoleId: p.playerRoleId,
          focus: p.focus,
        })),
      },
    };
  };

  const handleClickPitchButton = (
    action: "clear" | "import" | "save" | "share",
  ) => {
    switch (action) {
      case "clear":
        dispatch(clearSquad());
        break;
      case "import":
        handleImportSquad();
        break;
      case "save":
        const saveSquadData = defineSquadData({
          chosenTactic,
          benchItems,
          squad,
        });
        dispatch(showModal({ modalType: "save-squad", data: saveSquadData }));
        break;
      case "share":
        const shareSquadData = defineSquadData({
          chosenTactic,
          benchItems,
          squad,
        });
        dispatch(
          showModal({
            modalType: "share-squad",
            data: shareSquadData,
          }),
        );
        break;
    }
  };

  const handleCloseModal = () => {
    setShowModals(undefined);
    dispatch(setSaveSquadStatusToIdle());
  };

  const handleLoadSquad = (index: number) => {
    dispatch(
      fetchSpecificSquad({
        index,
        sharedOrSaved: "saved",
      }),
    );
  };

  const handleAddNewSquad = () => {
    dispatch(setSaveSquadStatusToIdle());
    dispatch(updateLoadedSquadIndex(undefined));
  };

  const handleDeleteSquad = async (squadId: string) => {
    await deleteSavedSquad(squadId);
    dispatch(updateLoadedSquadIndex(undefined));
    handleLoadSquads();
  };

  const handleGetTacticFromCode = (code: string) => {
    dispatch(getTacticFromCode({ code }));
  };

  const handleCopy = (eaCode: string) => {
    navigator.clipboard.writeText(eaCode).then(
      () => {
        dispatch(
          addAlert({
            title: "Success",
            description: "Copied to clipboard",
            type: "success",
          }),
        );
      },
      (err) => {
        console.error("Failed to copy: ", err);
      },
    );
  };

  const handleChangeTeamTactic = (key: string, value: string | number) => {
    if (chosenTactic) {
      dispatch(
        setChosenTactic({
          tactic: {
            ...chosenTactic,
            teamTactics: { ...chosenTactic.teamTactics, [key]: value },
          },
          fromTacticsPage: false,
        }),
      );
    }
  };

  const activeFilters: ActiveFilter[] = [];

  const addActiveFilter = (key: FilterKey, selectedFilters: any[]) => {
    if (selectedFilters?.length > 0) {
      activeFilters.push({ key, value: selectedFilters.length?.toString() });
    }
  };

  if (scope) activeFilters.push({ key: scope, value: "-" });
  addActiveFilter("country", selectedCountries);
  addActiveFilter("league", selectedLeagues);
  addActiveFilter("rarity", selectedVersions);
  addActiveFilter("clubs", selectedClubs);
  addActiveFilter("pastAndPresentClub", selectedPnPs);
  addActiveFilter("metal", selectedMetals);

  if (budget < Number.MAX_VALUE) {
    activeFilters.push({
      key: "budget",
      value: convertSBCPrice(budget),
    });
  }

  if (numberOfPlayersWithFilters > 0) {
    activeFilters.push({
      key: "playerFilter",
      value: numberOfPlayersWithFilters?.toString(),
    });
  }

  const handleClickMoreFilters = () =>
    dispatch(switchRightPanelMode("add-more-filters"));

  const handlePositionAndRoleChanged = (
    position: string,
    roleId: number,
    metaRatings: MetaRating[],
  ) => {
    dispatch(
      addPlayerOrChangeRole({ position, playerRoleId: roleId, metaRatings }),
    );
  };

  return (
    <MobileWrapper
      shouldWrap={!desktopMode}
      squad={squad}
      tempSquad={tempSquad}
      benchItems={benchItems}
      tactic={chosenTactic}
      activeBar={changingPitchItemOnIndex === -1 ? "default" : "playerActive"}
      playerActiveBar={playerActiveBar}
      handlePlayerActiveBar={handlePlayerActiveBar}
      onClickSquadSummaryMobile={handleClickSquadSummaryMobile}
      onClickFiltersSummaryMobile={handleClickFiltersSummaryMobile}
      onClickTacticSummaryMobile={handleClickTacticSummaryMobile}
      handleSelectManager={handleSelectManager}
      statusImportSquad={statusImportSquad}
      changingPitchItemOnIndex={changingPitchItemOnIndex}
      handleClickedCancelBuildSquads={handleClickedCancelBuildSquads}
      handleSelectedBuildSquads={handleSelectedBuildSquads}
      activeFilters={activeFilters}
      activeMenu={
        showLeftPanelMobile
          ? leftPanelMode === "tactics"
            ? "tactics"
            : "chem-overview"
          : showRightPanelMobile
            ? "default"
            : "none"
      }
      handleClickPitchButton={handleClickPitchButton}
      hasChangedSquad={hasChangedSquad}
      currentBuiltSquadsIndex={currentBuiltSquadsIndex}
      formationId={chosenTactic?.teamTactics?.formationId}
      hasLoadedSquad={loadedSaveSquadIndex !== undefined}
    >
      <div ref={refTopBar} className="h-1 bg-accent-foreground w-full" />
      <div
        className={clsx(
          "flex justify-center touch-device:w-auto touch-device:min-w-[1100px] desktop:min-h-[calc(115vh)] touch-device:max-h-[1200px] max-w-[2000px] mx-auto relative",
        )}
      >
        {showLeftPanel && (
          <LeftPanel
            formations={formations}
            squad={squad}
            tempSquad={tempSquad}
            chosenTactic={chosenTactic}
            formationLabel={formationLabel}
            mode={"default"}
            desktopMode={desktopMode}
            leftPanelMode={leftPanelMode}
            handleSwitchLeftPanelMode={handleSwitchLeftPanelMode}
            handleCopy={() => handleCopy(chosenTactic?.eaCode)}
            handleChangeBuildUpStyle={(buildUpStyle) =>
              handleChangeTeamTactic("buildUpStyle", buildUpStyle)
            }
            handleChangeDefensiveApproach={(defensiveApproach) =>
              handleChangeTeamTactic("defensiveApproach", defensiveApproach)
            }
            handleChangeFormation={(formationId: string) =>
              handleChangeTeamTactic("formationId", formationId)
            }
            handleChangeLineHeight={(lineHeight) =>
              handleChangeTeamTactic("lineHeight", lineHeight)
            }
            handleGetTacticFromCode={handleGetTacticFromCode}
            statusInsertTacticCode={statusInsertTacticCode}
            scrollPastTopBar={scrollPastTopBar}
          />
        )}
        <CenterPanel
          mode={"default"}
          squad={squad}
          tempSquad={tempSquad}
          benchItems={benchItems}
          tempBenchItems={tempBenchItems}
          statusBuildSquad={statusBuildSquad}
          changingPitchItemOnIndex={changingPitchItemOnIndex}
          handleAddPlayer={handleAddPlayer}
          handleMovePlayer={handleMovePlayer}
          handleChangePlayerRole={handleChangePlayerRole}
          rightPanelMode={rightPanelMode}
          handleRemovePlayerOrRole={handleRemovePlayerOrRole}
          handleAddPlayerFilters={handleAddPlayerFilters}
          handleClearPlayerFilters={handleClearPlayerFilters}
          playerRoles={playerRoles}
          playerFilters={playerFilters}
          statusImportSquad={statusImportSquad}
          handleSelectManager={handleSelectManager}
          handleChangeChemstyle={handleChangeChemstyle}
          handleAddEvoPlayer={handleAddEvoPlayer}
          handleCloseLoadingModal={handleCloseSuggestedSquadsMode}
          handleOnClickPlayer={handleOnClickPlayer}
          handleOnClickPlayerMobile={handleOnClickPlayerMobile}
          tabletOrLargerMode={tabletOrLargerMode}
          desktopMode={desktopMode}
          mobileHideCenter={
            (showLeftPanelMobile || showRightPanelMobile) && !tabletOrLargerMode
          }
          handleClickedBuildSquads={handleClickedBuildSquads}
          disablePlayerInteractions={currentBuiltSquadsIndex >= 0}
          handleOnClickPitchMobile={
            desktopMode ? undefined : handleOnClickPitchMobile
          }
          handleClickPitchButton={handleClickPitchButton}
          handleCloseModal={handleCloseModal}
          showModals={showModals}
          chosenTactic={chosenTactic}
          hasChangedSquad={hasChangedSquad}
          currentBuiltSquadsIndex={currentBuiltSquadsIndex}
          changeBuiltSquadsIndex={handleCurrentBuiltSquadsIndex}
          totalAmountOfBuiltSquads={builtSquads.length || 0}
          tempFormation={tempFormation}
          hasLoadedSquad={loadedSaveSquadIndex !== undefined}
        />
        {showRightPanel && (
          <RightPanel
            mode={"default"}
            statusBuildSquad={statusBuildSquad}
            budget={budget}
            excludeIconsHeroes={excludeIconsHeroes}
            ratingFilter={ratingFilter}
            scope={scope}
            selectedCountries={selectedCountries}
            selectedLeagues={selectedLeagues}
            selectedClubs={selectedClubs}
            selectedPnPs={selectedPnPs}
            selectedVersions={selectedVersions}
            playersToInclude={[]}
            selectedMetals={selectedMetals}
            recommendedPlayers={recommendedPlayers}
            changingPitchItemOnIndex={changingPitchItemOnIndex}
            handleDidAddPlayer={handleDidAddPlayer}
            statusGetRecommendedPlayers={statusGetRecommendedPlayers}
            handleClickedClose={handleClickedClose}
            handleClickedClosePlayerFiltersMode={
              handleClickedClosePlayerFiltersMode
            }
            handleClickedCloseMoreFiltersMode={
              handleClickedCloseMoreFiltersMode
            }
            activeFilters={activeFilters}
            numberOfPlayersWithFilters={numberOfPlayersWithFilters}
            handleClearFilter={handleClearFilter}
            handleGetPlayerRecommendations={handleGetPlayerRecommendations}
            playerRoles={playerRoles}
            playerFilters={playerFilters[changingPitchItemOnIndex]}
            handleBuildSquadFulfilled={handleBuildSquadFulfilled}
            handleUpdateFormation={handleUpdateFormation}
            handleClickedBuildSquads={handleClickedBuildSquads}
            rightPanelMode={rightPanelMode}
            handleUpdateFilter={handleUpdateFilter}
            handleClearCustomFilter={handleClearCustomFilter}
            handleScopeChange={handleScopeChange}
            handleSelectedCountry={handleSelectedCountry}
            handleSelectedLeague={handleSelectedLeague}
            handleSelectedClub={handleSelectedClub}
            handleSelectedPnP={handleSelectedPastAndPresent}
            handleSelectedMetal={handleSelectedMetal}
            handleSelectedVersion={handleSelectedVersion}
            handleBudgetUpdated={handleBudgetUpdated}
            handleExcludeIconsHeroesUpdated={handleExcludeIconsHeroesUpdated}
            handleMinRatingUpdated={handleMinRatingUpdated}
            handleMaxRatingUpdated={handleMaxRatingUpdated}
            handleRemoveTempPlayer={handleRemoveTempPlayer}
            handleSortBuiltSquads={handleSortBuiltSquads}
            handleRemoveTempSquad={handleRemoveTempSquad}
            handleAddSquad={handleAddSquad}
            builtSquads={builtSquads}
            squad={squad}
            useFormation={"chem-formation"}
            handleClickedCloseSuggestedSquadsMode={
              handleCloseSuggestedSquadsMode
            }
            handleSelectChemstyle={handleSelectChemstyle}
            handleClickedCloseChemstyleMode={handleClickedCloseChemstyleMode}
            handleClickedCloseEvolvePlayerMode={
              handleClickedCloseEvolvePlayerMode
            }
            tabletOrLargerMode={tabletOrLargerMode}
            desktopMode={desktopMode}
            savedSquads={savedSquads}
            loadedSaveSquadIndex={loadedSaveSquadIndex}
            handleLoadSquad={handleLoadSquad}
            handleLoadSquads={handleLoadSquads}
            handleAddNewSquad={handleAddNewSquad}
            handleDeleteSquad={handleDeleteSquad}
            chosenTactic={chosenTactic}
            handleClickMoreFilters={handleClickMoreFilters}
            scrollPastTopBar={scrollPastTopBar}
            benchItems={benchItems}
            handlePositionAndRoleChanged={handlePositionAndRoleChanged}
            includeLoanPlayers={includeLoanPlayers}
            handleIncludeLoanPlayersUpdated={handleIncludeLoanPlayersUpdated}
          />
        )}
      </div>
    </MobileWrapper>
  );
};
